import { CylinderGeometry, Material, Mesh, MeshBasicMaterial, Object3D, Vector3 } from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

const _axis = /*@__PURE__*/ new Vector3();

class ArrowHelper extends Object3D {
  _lineGeometry: LineGeometry;
  _coneGeometry!: CylinderGeometry;
  _lineMaterial!: LineMaterial;

  // dir is assumed to be normalized
  override type: string;
  line: Line2;
  cone: Mesh;

  constructor(
    public shape: 'line' | 'circle' = 'line',
    dir = new Vector3(0, 0, 1),
    origin = new Vector3(0, 0, 0),
    length = 1,
    color = 0xffff00,
    headLength = length * 0.2,
    headWidth = headLength * 0.2,
    isForAccelerationArrow = false
  ) {
    super();

    this.type = 'ArrowHelper';

    this._lineGeometry = new LineGeometry();
    this._lineMaterial = new LineMaterial({
      color: color,
      toneMapped: false,
      linewidth: 3,
      depthTest: false,
      alphaTest: 0,
      transparent: true
    });
    this._lineMaterial.resolution.set(1024, 1024);

    if (shape === 'circle') {
      this._coneGeometry = new CylinderGeometry(0, 2, 6, 16, 1);

      let radius = 0.5;
      let thetaStart = 0;
      let thetaLength = Math.PI * 1.5;
      let vertices = [];
      let segments = 64;
      const vertex = new Vector3();
      for (let s = 0, i = 3; s <= segments; s++, i += 3) {
        const segment = thetaStart + (s / segments) * thetaLength;
        vertex.x = radius * Math.cos(segment);
        vertex.y = radius * Math.sin(segment);
        vertices.push(vertex.x, vertex.y, vertex.z);
      }
      this._lineGeometry.setPositions(vertices);
      this._lineGeometry.rotateZ(-Math.PI);
      this._lineGeometry.rotateX(Math.PI * 0.5);
      this._coneGeometry.rotateZ(Math.PI * 0.5);
      this._coneGeometry.translate(0, 0, 10);
    } else {
      this._coneGeometry = new CylinderGeometry(0, 1, 1, 16, 1);
      this._lineGeometry.setPositions([0, 0, 0, 0, 0.5, 0]);
      this._coneGeometry.translate(0, -0.5, 0);

      if (isForAccelerationArrow) {
        this._lineGeometry.translate(0, -0.25, 0);
        this._coneGeometry.translate(0, -1, 0);
      }
    }

    this.position.copy(origin);

    this.line = new Line2(this._lineGeometry, this._lineMaterial);
    this.line.computeLineDistances();
    this.line.scale.set(1, 1, 1);
    this.line.matrixAutoUpdate = false;
    this.add(this.line);

    this.cone = new Mesh(
      this._coneGeometry,
      new MeshBasicMaterial({ color: color, toneMapped: false, depthTest: false, alphaTest: 0, transparent: true })
    );
    this.cone.matrixAutoUpdate = false;
    this.add(this.cone);

    this.line.layers.set(1);
    this.cone.layers.set(1);

    this.setDirection(dir);
    this.setLength(length, headLength, headWidth);

    if (isForAccelerationArrow) {
        this.position.z = 1.5;
    }
  }

  setMaterials(lineMaterial: LineMaterial, coneMaterial: Material) {
    this.line.material = lineMaterial;
    this.cone.material = coneMaterial;
  }

  setDirection(dir: Vector3) {
    // dir is assumed to be normalized

    dir.normalize();

    if (dir.y > 0.99999) {
      this.quaternion.set(0, 0, 0, 1);
    } else if (dir.y < -0.99999) {
      this.quaternion.set(1, 0, 0, 0);
    } else {
      _axis.set(dir.z, 0, -dir.x).normalize();

      const radians = Math.acos(dir.y);

      const newQuat = this.quaternion.clone().setFromAxisAngle(_axis, radians);

      this.quaternion.copy(newQuat);
      // new Tween(this.quaternion).to(newQuat).duration(100).start();
    }
  }

  setLength(length: number, headLength = length * 0.2, headWidth = headLength * 0.2) {
    if (this.shape === 'circle') {
      this.line.scale.setScalar(length * 0.5); // see #17458
      this.line.updateMatrix();
    } else {
      this.line.scale.set(1, Math.max(0.0001, length - headLength), 1); // see #17458
      this.line.updateMatrix();
    }

    if (this.shape === 'circle') {
      const k = length / 40;
      this.cone.scale.setScalar(k);
      this.cone.updateMatrix();
    } else {
      this.cone.scale.set(headWidth, headLength, headWidth);
      this.cone.position.y = length / 2;
      this.cone.updateMatrix();
    }
  }

  setColor(color: any) {
    (this.line.material as any).color.set(color);
    (this.cone.material as any).color.set(color);
  }

  override copy(source: this) {
    super.copy(source, false);

    this.line.copy(source.line);
    this.cone.copy(source.cone);

    return this;
  }

  dispose() {
    this.line.geometry.dispose();
    (this.line.material as any).dispose();
    this.cone.geometry.dispose();
    (this.cone.material as any).dispose();
  }
}

export { ArrowHelper };
